<template>
  <div class="mt-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <strong><i class="fa fa-list me-2"></i>Ítems Solicitados</strong>
      <div>
        <span class="badge bg-info me-2">{{ solicitud.detalles?.length || 0 }} {{ (solicitud.detalles?.length || 0) === 1 ? 'ítem' : 'ítems' }}</span>
        <BButton
          variant="danger"
          size="sm"
          class="me-2"
          @click="generarPdfEspecificaciones"
          :disabled="generandoPdf"
          :title="solicitud.tipo_contratacion === 'BIENES' ? 'Generar Especificaciones Técnicas' : 'Generar Términos de Referencia (TDR)'"
        >
          <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa fa-file-pdf"></i>
          {{ solicitud.tipo_contratacion === 'BIENES' ? 'Espec. Técnicas' : 'TDR' }}
        </BButton>
        <BButton v-if="canEdit" variant="success" size="sm" @click="openDetalleModal()">
          <i class="fa fa-plus me-1"></i> Agregar Ítem
        </BButton>
      </div>
    </div>

    <div v-if="!solicitud.detalles?.length" class="alert alert-info">
      No hay ítems registrados en esta solicitud
    </div>

    <div v-else class="table-responsive">
      <table class="table table-sm table-bordered table-hover">
        <thead class="table-light">
          <tr style="background-color: #f5f5f5; font-size:x-small;">
            <th>#</th>
            <th>Código Ítem</th>
            <th>Nombre Ítem</th>
            <th>Detalle</th>
            <th>Und.Medida</th>
            <th>Partida</th>
            <th>Cantidad</th>
            <th>C.Unitario</th>
            <th>Subtotal</th>
            <th v-if="canEdit">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(detalle, index) in solicitud.detalles" :key="detalle.id">
            <td class="text-center">{{ index + 1 }}</td>
            <td><strong>{{ detalle.item?.codigo || detalle.id_item }}</strong></td>
            <td>{{ detalle.item?.nombre_item || 'No especificado' }}</td>
            <td>{{ detalle.detalle || '—' }}</td>
            <td>{{ detalle.item?.unidad_medida || 'N/A' }}</td>
            <td>
              <span v-if="detalle.partida">
                <strong>{{ detalle.partida.codigo }}</strong>
                <small class="text-muted d-block">{{ truncate(detalle.partida.nombre, 40) }}</small>
              </span>
              <span v-else-if="detalle.id_partida">{{ detalle.id_partida }}</span>
              <span v-else class="text-muted">N/A</span>
            </td>
            <td class="text-end">{{ detalle.cantidad }}</td>
            <td class="text-end">Bs. {{ formatNumber(parseFloat(detalle.costo_unitario) || 0) }}</td>
            <td class="text-end text-primary">
              <strong>Bs. {{ calcularSubtotal(detalle) }}</strong>
            </td>
            <td v-if="canEdit" class="text-center">
              <BButton variant="warning" size="sm" @click="editDetalle(detalle)" class="me-1 btn-icon-xs" title="Editar">
                <i class="fa fa-edit"></i>
              </BButton>
              <BButton variant="danger" size="sm" @click="confirmDeleteDetalle(detalle)" class="me-1 btn-icon-xs" title="Eliminar">
                <i class="fa fa-trash"></i>
              </BButton>
            </td>
          </tr>
          <tr class="table-active">
            <td colspan="8" class="text-end"><strong>TOTAL GENERAL:</strong></td>
            <td class="text-end"><strong class="text-success">Bs. {{ totalDetalle }}</strong></td>
            <td v-if="canEdit"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- MODAL PARA AGREGAR/EDITAR DETALLE -->
    <BModal v-model="detalleModalVisible" :title="detalleModalTitle" size="lg" centered hide-footer>
      <form @submit.prevent="saveDetalle">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-3">
              <label class="form-label">Ítem *</label>
              <div class="position-relative">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': detalleErrors.id_item }"
                  v-model="itemSearchText"
                  placeholder="Buscar por código o nombre del ítem..."
                  @input="onItemSearch"
                  @focus="onItemFocus"
                  @blur="hideSuggestions"
                  @keydown.down.prevent="selectNextSuggestion"
                  @keydown.up.prevent="selectPrevSuggestion"
                  @keydown.enter.prevent="applySelectedSuggestion"
                  autocomplete="off"
                />
                <i class="fa fa-search position-absolute" style="right: 12px; top: 50%; transform: translateY(-50%); color: #6c757d;"></i>

                <!-- Lista de sugerencias -->
                <div v-if="showSuggestions && filteredItems.length > 0" class="suggestions-list">
                  <div
                    v-for="(item, index) in filteredItems"
                    :key="item.codigo"
                    class="suggestion-item"
                    :class="{ active: selectedSuggestionIndex === index }"
                    @mousedown.prevent="selectSuggestion(item)"
                    @mouseenter="selectedSuggestionIndex = index"
                  >
                    <div class="d-flex align-items-center">
                      <div>
                        <span>{{ item.codigo }} - {{ item.nombre_item }} ({{ item.unidad_medida || 'N/A' }})</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="showSuggestions && itemSearchText.length > 1 && filteredItems.length === 0" class="suggestions-list">
                  <div class="suggestion-item text-muted text-center py-2">
                    <i class="fa fa-exclamation-circle me-2"></i>
                    No se encontraron ítems con "{{ itemSearchText }}"
                  </div>
                </div>
              </div>
              <div class="invalid-feedback" v-if="detalleErrors.id_item">{{ detalleErrors.id_item[0] }}</div>
              <small class="text-muted">Escribe para buscar por código o nombre del ítem</small>
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group mb-3">
                <label class="form-label">Detalle / Especificación</label>
                <textarea
                v-model="detalleForm.detalle"
                class="form-control"
                :class="{ 'is-invalid': detalleErrors.detalle }"
                rows="3"
                maxlength="500"
                placeholder="Descripción adicional, especificaciones técnicas, etc."
                ></textarea>
                <div class="invalid-feedback" v-if="detalleErrors.detalle">{{ detalleErrors.detalle[0] }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label class="form-label">Cantidad *</label>
              <input
                v-model="detalleForm.cantidad"
                type="text"
                class="form-control"
                placeholder="Ej: 10 unidades"
                :class="{ 'is-invalid': detalleErrors.cantidad }"
                required
              >
              <div class="invalid-feedback" v-if="detalleErrors.cantidad">{{ detalleErrors.cantidad[0] }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label class="form-label">Costo Unitario (Bs) *</label>
              <input
                v-model="detalleForm.costo_unitario"
                type="number"
                step="0.01"
                min="0"
                class="form-control"
                :class="{ 'is-invalid': detalleErrors.costo_unitario }"
                required
              >
              <div class="invalid-feedback" v-if="detalleErrors.costo_unitario">{{ detalleErrors.costo_unitario[0] }}</div>
            </div>
          </div>
          <!-- Información del ítem seleccionado -->
          <div v-if="selectedItemInfo" class="col-12 mb-2">
            <div class="alert alert-info p-2 d-flex justify-content-between align-items-center">
              <div>
                <i class="fa fa-check-circle text-success me-2"></i>
                <strong>Ítem seleccionado:</strong>
                <span class="">{{ selectedItemInfo.codigo }}</span>
                {{ selectedItemInfo.nombre_item }}
                <span v-if="selectedItemInfo.unidad_medida" class="ms-2">
                  <strong>Unidad:</strong> {{ selectedItemInfo.unidad_medida }}
                </span>
              </div>
              <BButton size="sm" variant="outline-secondary" @click="clearSelectedItem" title="Limpiar selección">
                <i class="fa fa-times"></i>
              </BButton>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <BButton type="button" variant="secondary" @click="closeModal">Cancelar</BButton>
          <BButton type="submit" variant="primary" :disabled="loadingDetalle || !detalleForm.id_item">
            <span v-if="loadingDetalle" class="spinner-border spinner-border-sm" role="status"></span>
            {{ detalleForm.id ? 'Actualizar' : 'Agregar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { BButton, BModal } from 'bootstrap-vue-3';
import axios from 'axios';
import Swal from 'sweetalert2';

const props = defineProps({
  solicitud: { type: Object, required: true },
  canEdit: { type: Boolean, default: false }
});

const emit = defineEmits(['detalle-added', 'detalle-updated', 'detalle-deleted']);

// Datos principales
const items = ref([]);
const partidas = ref([]);
const detalleModalVisible = ref(false);
const detalleForm = ref({ id: null, id_item: '', id_partida: '', cantidad: '', costo_unitario: '', detalle: '' });
const detalleErrors = ref({});
const loadingDetalle = ref(false);
const generandoPdf = ref(false);

// Búsqueda de ítems
const itemSearchText = ref('');
const filteredItems = ref([]);
const showSuggestions = ref(false);
const selectedSuggestionIndex = ref(-1);
const selectedItemInfo = ref(null);

// Búsqueda de partidas
const partidaSearchText = ref('');
const filteredPartidas = ref([]);
const showPartidaSuggestions = ref(false);
const selectedPartidaIndex = ref(-1);

const detalleModalTitle = computed(() => detalleForm.value.id ? 'Editar Ítem' : 'Agregar Ítem');

const totalDetalle = computed(() => {
  if (!props.solicitud?.detalles?.length) return formatNumber(0);
  const total = props.solicitud.detalles.reduce((sum, detalle) => {
    const cantidad = parseFloat(detalle.cantidad) || 0;
    const costoUnitario = parseFloat(detalle.costo_unitario) || 0;
    return sum + (cantidad * costoUnitario);
  }, 0);
  return formatNumber(total);
});

// ==================== MÉTODOS DE BÚSQUEDA DE ÍTEMS ====================
const onItemSearch = (event) => {
  const search = event.target.value.toLowerCase().trim();
  selectedSuggestionIndex.value = -1;

  if (!search || search.length < 1) {
    filteredItems.value = items.value.slice(0, 20);
    showSuggestions.value = true;
    return;
  }

  filteredItems.value = items.value.filter(item =>
    item.codigo.toLowerCase().includes(search) ||
    item.nombre_item.toLowerCase().includes(search)
  ).slice(0, 50);

  showSuggestions.value = true;
};

const onItemFocus = () => {
  showSuggestions.value = true;
  if (filteredItems.value.length === 0) {
    filteredItems.value = items.value.slice(0, 20);
  }
};

const hideSuggestions = () => {
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
};

const selectNextSuggestion = () => {
  if (selectedSuggestionIndex.value < filteredItems.value.length - 1) {
    selectedSuggestionIndex.value++;
  }
};

const selectPrevSuggestion = () => {
  if (selectedSuggestionIndex.value > 0) {
    selectedSuggestionIndex.value--;
  }
};

const applySelectedSuggestion = () => {
  if (selectedSuggestionIndex.value >= 0 && selectedSuggestionIndex.value < filteredItems.value.length) {
    selectSuggestion(filteredItems.value[selectedSuggestionIndex.value]);
  }
};

const selectSuggestion = (item) => {
  detalleForm.value.id_item = item.id;
  selectedItemInfo.value = item;
  // Mostrar código y nombre en el input
  itemSearchText.value = `${item.codigo} - ${item.nombre_item}`;
  showSuggestions.value = false;

  // Si el ítem tiene partida y no hay partida seleccionada
  if (item.partida?.codigo && !detalleForm.value.id_partida) {
    detalleForm.value.id_partida = item.partida.codigo;
    // Mostrar la partida en el campo de búsqueda
    const partida = partidas.value.find(p => p.codigo === item.partida.codigo);
    if (partida) {
      partidaSearchText.value = `${partida.codigo} - ${partida.nombre}`;
    }
  }
};

const clearSelectedItem = () => {
  detalleForm.value.id_item = '';
  selectedItemInfo.value = null;
  itemSearchText.value = '';
  filteredItems.value = items.value.slice(0, 20);
};

// ==================== MÉTODOS DE BÚSQUEDA DE PARTIDAS ====================
const onPartidaSearch = (event) => {
  const search = event.target.value.toLowerCase().trim();
  selectedPartidaIndex.value = -1;

  if (!search || search.length < 1) {
    filteredPartidas.value = partidas.value.slice(0, 20);
    showPartidaSuggestions.value = true;
    return;
  }

  filteredPartidas.value = partidas.value.filter(partida =>
    partida.codigo.toLowerCase().includes(search) ||
    partida.nombre.toLowerCase().includes(search)
  ).slice(0, 30);

  showPartidaSuggestions.value = true;
};

const onPartidaFocus = () => {
  showPartidaSuggestions.value = true;
  if (filteredPartidas.value.length === 0) {
    filteredPartidas.value = partidas.value.slice(0, 20);
  }
};

const hidePartidaSuggestions = () => {
  setTimeout(() => {
    showPartidaSuggestions.value = false;
  }, 200);
};

const selectNextPartida = () => {
  if (selectedPartidaIndex.value < filteredPartidas.value.length - 1) {
    selectedPartidaIndex.value++;
  }
};

const selectPrevPartida = () => {
  if (selectedPartidaIndex.value > 0) {
    selectedPartidaIndex.value--;
  }
};

const applySelectedPartida = () => {
  if (selectedPartidaIndex.value >= 0 && selectedPartidaIndex.value < filteredPartidas.value.length) {
    selectPartida(filteredPartidas.value[selectedPartidaIndex.value]);
  }
};

const selectPartida = (partida) => {
  detalleForm.value.id_partida = partida.codigo;
  partidaSearchText.value = `${partida.codigo} - ${partida.nombre}`;
  showPartidaSuggestions.value = false;
};

// ==================== MÉTODOS PRINCIPALES ====================
const generarPdfEspecificaciones = async () => {
  generandoPdf.value = true;
  try {
    const response = await axios.get(`/solicitud-contratacion/pdf/especificaciones-tecnicas/${props.solicitud.id}`, {
      responseType: 'blob'
    });

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const nombreArchivo = props.solicitud.tipo_contratacion === 'BIENES'
      ? `Especificaciones_Tecnicas_${props.solicitud.codigo}.pdf`
      : `Terminos_Referencia_${props.solicitud.codigo}.pdf`;

    link.href = blobUrl;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);

    Swal.fire('Éxito', 'Documento generado correctamente', 'success');
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire('Error', error.response?.data?.message || 'No se pudo generar el documento', 'error');
  } finally {
    generandoPdf.value = false;
  }
};

const loadItems = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/items');
    items.value = response.data.data;
    filteredItems.value = items.value.slice(0, 20);
  } catch (error) {
    console.error('Error al cargar ítems:', error);
  }
};

const loadPartidas = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/partidas');
    partidas.value = response.data.data;
    filteredPartidas.value = partidas.value.slice(0, 20);
  } catch (error) {
    console.error('Error al cargar partidas:', error);
  }
};

const openDetalleModal = (detalle = null) => {
  detalleErrors.value = {};
  detalleForm.value = {
    id: null,
    id_item: '',
    id_partida: '',
    cantidad: '',
    costo_unitario: '',
    detalle: ''
  };
  itemSearchText.value = '';
  selectedItemInfo.value = null;
  partidaSearchText.value = '';
  selectedPartidaIndex.value = -1;

  if (detalle) {
    detalleForm.value = {
      id: detalle.id,
      id_item: detalle.id_item,
      id_partida: detalle.id_partida || '',
      cantidad: detalle.cantidad,
      costo_unitario: detalle.costo_unitario,
      detalle: detalle.detalle || ''
    };

    // Buscar el ítem seleccionado para mostrar su información
    const foundItem = items.value.find(i => i.codigo === detalle.id_item);
    if (foundItem) {
      selectedItemInfo.value = foundItem;
      itemSearchText.value = `${foundItem.codigo} - ${foundItem.nombre_item}`;
    }

    // Buscar la partida seleccionada
    if (detalle.id_partida) {
      const foundPartida = partidas.value.find(p => p.codigo === detalle.id_partida);
      if (foundPartida) {
        partidaSearchText.value = `${foundPartida.codigo} - ${foundPartida.nombre}`;
      }
    }
  }
  detalleModalVisible.value = true;
};

const editDetalle = (detalle) => openDetalleModal(detalle);

const saveDetalle = async () => {
  loadingDetalle.value = true;
  detalleErrors.value = {};

  if (!detalleForm.value.id_item) {
    detalleErrors.value.id_item = ['Debe seleccionar un ítem'];
    loadingDetalle.value = false;
    return;
  }

  try {
    if (detalleForm.value.id) {
      const response = await axios.put(`/solicitud-contratacion/${props.solicitud.id}/detalles/${detalleForm.value.id}`, detalleForm.value);
      const index = props.solicitud.detalles.findIndex(d => d.id === response.data.data.id);
      if (index !== -1) {
        props.solicitud.detalles[index] = response.data.data;
      }
      Swal.fire('Éxito', 'Ítem actualizado', 'success');
      emit('detalle-updated', response.data.data);
    } else {
      const response = await axios.post(`/solicitud-contratacion/${props.solicitud.id}/detalles`, detalleForm.value);
      if (!props.solicitud.detalles) props.solicitud.detalles = [];
      props.solicitud.detalles.push(response.data.data);
      Swal.fire('Éxito', 'Ítem agregado', 'success');
      emit('detalle-added', response.data.data);
    }
    closeModal();
  } catch (error) {
    if (error.response?.status === 422) {
      detalleErrors.value = error.response.data.errors;
    } else {
      Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
    }
  } finally {
    loadingDetalle.value = false;
  }
};

const closeModal = () => {
  detalleModalVisible.value = false;
  detalleForm.value = { id: null, id_item: '', id_partida: '', cantidad: '', costo_unitario: '', detalle: '' };
  itemSearchText.value = '';
  selectedItemInfo.value = null;
  partidaSearchText.value = '';
  detalleErrors.value = {};
  showSuggestions.value = false;
  showPartidaSuggestions.value = false;
};

const confirmDeleteDetalle = (detalle) => {
  Swal.fire({
    title: '¿Eliminar ítem?',
    text: `¿Estás seguro de eliminar "${detalle.item?.nombre_item || detalle.id_item}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await axios.delete(`/solicitud-contratacion/${props.solicitud.id}/detalles/${detalle.id}`);
        props.solicitud.detalles = props.solicitud.detalles.filter(d => d.id !== detalle.id);
        Swal.fire('Éxito', 'Ítem eliminado', 'success');
        emit('detalle-deleted', detalle.id);
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Error al eliminar', 'error');
      }
    }
  });
};

const formatNumber = (n) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2 }).format(n || 0);
const truncate = (text, length) => text?.length > length ? text.substring(0, length) + '...' : text;
const calcularSubtotal = (detalle) => formatNumber((parseFloat(detalle.cantidad) || 0) * (parseFloat(detalle.costo_unitario) || 0));

// Watch para mantener actualizadas las listas de sugerencias
watch(items, (newItems) => {
  filteredItems.value = newItems.slice(0, 20);
});

watch(partidas, (newPartidas) => {
  filteredPartidas.value = newPartidas.slice(0, 20);
});

onMounted(() => {
  loadItems();
  loadPartidas();
});
</script>

<style scoped>
.table-responsive { max-height: 400px; overflow-y: auto; }

/* Estilos para el autocomplete */
.suggestions-list {
  position: absolute;
  z-index: 1060;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 0 0 0.375rem 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  margin-top: 2px;
}

.suggestion-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s;
}

.suggestion-item:hover,
.suggestion-item.active {
  background-color: #e7f1ff;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item .badge {
  font-size: 0.7rem;
}

/* Scroll personalizado */
.suggestions-list::-webkit-scrollbar {
  width: 6px;
}

.suggestions-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.suggestions-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.suggestions-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Estilos para el input con icono de búsqueda */
.position-relative .fa-search {
  pointer-events: none;
}
</style>
